import React, { useContext, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CustomDialog, CustomSortableTable, DashboardLayout, SortableTableRow } from '@blumtechgroup/blum-react-core-components';
import { StringUtils } from '@blumtechgroup/blum-ui-utils';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { userLookupQuery, UserLookupResponse, UserLookupVariables } from './queries';
import { AuthContext, EnumUserRole } from '../../../components/AuthProvider';
import { webhookClient } from '../../../services/webhookClient';

const NoOrganisationLabel = '—';

interface UserLookupRow {
  key: string;
  // Only populated for rows sourced from users_clinician — the reset/delete
  // webhook endpoints only work against clinicians (they 404 on the other
  // three tables), so this is also what gates whether a row gets actions.
  clinicianId?: string;
  name: string;
  email: string;
  role: string;
  organisation: string;
  location: string;
  active: boolean;
}

const buildRows = (data?: UserLookupResponse): UserLookupRow[] => {
  if (!data) {
    return [];
  }

  return [
    ...data.users_admin.map((item) => ({
      key: `users_admin-${item.id}`,
      name: StringUtils.stringFromArray([item.first_name, item.last_name]),
      email: item.email,
      role: 'Global Admin',
      organisation: NoOrganisationLabel,
      location: NoOrganisationLabel,
      active: item.active,
    })),
    ...data.users_organisation_admin.map((item) => ({
      key: `users_organisation_admin-${item.id}`,
      name: StringUtils.stringFromArray([item.first_name, item.last_name]),
      email: item.email,
      role: 'Organisation Admin',
      organisation: item.organisation.name,
      location: NoOrganisationLabel,
      active: item.active,
    })),
    ...data.users_location_admin.map((item) => ({
      key: `users_location_admin-${item.id}`,
      name: StringUtils.stringFromArray([item.first_name, item.last_name]),
      email: item.email,
      role: 'Location Admin',
      organisation: item.organisation.name,
      location: item.location.name,
      active: item.active,
    })),
    ...data.users_clinician.map((item) => ({
      key: `users_clinician-${item.id}`,
      clinicianId: item.id,
      name: StringUtils.stringFromArray([item.first_name, item.last_name]),
      email: item.email,
      role: 'Clinician',
      organisation: item.organisation.name,
      location: item.location.name,
      active: item.active,
    })),
  ];
};

const UserLookup = (): React.ReactElement => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [submittedTerm, setSubmittedTerm] = useState<string>('');

  const [runSearch, { data, loading }] = useLazyQuery<UserLookupResponse, UserLookupVariables>(userLookupQuery, { fetchPolicy: 'no-cache' });

  // Only the global admin ("super admin") may reset another user's access (BLUM-7066).
  // The webhook enforces this too; this gate just hides the control for everyone else.
  const authContext = useContext(AuthContext);
  const isAdmin = authContext?.userRole === EnumUserRole.ADMIN;

  const [resetState, setResetState] = useState<{ userId: string; name: string; confirmOpen: boolean }>({
    userId: '',
    name: '',
    confirmOpen: false,
  });
  const [resetting, setResetting] = useState<boolean>(false);
  const [resultDialog, setResultDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const [deleteState, setDeleteState] = useState<{ userId: string; name: string; confirmOpen: boolean }>({
    userId: '',
    name: '',
    confirmOpen: false,
  });
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleSearch = () => {
    const term = searchValue.trim();
    if (!term) {
      return;
    }
    setSubmittedTerm(term);
    runSearch({ variables: { term: `%${term}%` } });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  // Re-runs the current search so results reflect the latest state (e.g. after a delete).
  const refreshResults = () => {
    if (submittedTerm) {
      runSearch({ variables: { term: `%${submittedTerm}%` } });
    }
  };

  const handleResetAccess = async () => {
    if (resetting) {
      return;
    }
    const { userId } = resetState;
    setResetting(true);
    // Close the confirm dialog immediately so it can't be double-submitted.
    setResetState((prev) => ({ ...prev, confirmOpen: false }));
    try {
      const res = await webhookClient.postWithData('admin/reset-user-access', { userId });
      const message =
        res && res.success
          ? res.action === 'resend_invite'
            ? 'Invitation email resent. The user can set a new password from the link.'
            : 'Password reset email sent. The user can set a new password from the link.'
          : `Could not reset access${res && res.message ? `: ${res.message}` : ''}.`;
      setResultDialog({ open: true, message });
    } catch (err) {
      setResultDialog({ open: true, message: 'Could not reset access. Please try again.' });
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (deleting) {
      return;
    }
    const { userId } = deleteState;
    setDeleting(true);
    // Close the confirm dialog immediately so it can't be double-submitted.
    setDeleteState((prev) => ({ ...prev, confirmOpen: false }));
    try {
      const res = await webhookClient.postWithData('admin/delete-user', { userId });
      if (res && res.success) {
        setResultDialog({ open: true, message: 'User permanently deleted, including their login.' });
        refreshResults();
      } else {
        // Includes the "has clinical records, cannot delete" guard-rail message from the API.
        setResultDialog({ open: true, message: res && res.message ? res.message : 'Could not delete user.' });
      }
    } catch (err) {
      setResultDialog({ open: true, message: 'Could not delete user. Please try again.' });
    } finally {
      setDeleting(false);
    }
  };

  const rows = buildRows(data);

  const tableRows: SortableTableRow[] = rows.map((row) => {
    // Belt-and-braces: only clinician rows are actionable (the backend endpoints 404 on the
    // other three tables), and only for the global admin.
    const actionable = isAdmin && !!row.clinicianId;

    return {
      key: row.key,
      columns: [
        { key: 'name', label: row.name },
        { key: 'email', label: row.email },
        { key: 'role', label: row.role },
        { key: 'organisation', label: row.organisation },
        { key: 'location', label: row.location },
        { key: 'active', label: StringUtils.booleanToYesNo(row.active) },
      ],
      ...(actionable
        ? {
            actions: [
              {
                label: 'Reset password',
                onClick: () =>
                  setResetState({
                    userId: row.clinicianId!,
                    name: row.name,
                    confirmOpen: true,
                  }),
              },
              {
                label: 'Delete user',
                destructive: true,
                onClick: () =>
                  setDeleteState({
                    userId: row.clinicianId!,
                    name: row.name,
                    confirmOpen: true,
                  }),
              },
            ],
          }
        : {}),
    };
  });

  return (
    <DashboardLayout breadcrumbs={[{ label: 'Admin', link: '/admin/home' }, { label: 'User Lookup' }]}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Email or name"
                value={searchValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value || '')}
                onKeyDown={handleKeyDown}
                inputProps={{ 'aria-label': 'search users' }}
              />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleSearch} disabled={loading || !searchValue.trim()}>
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {!submittedTerm && <Typography>Enter an email or name to search.</Typography>}
          {!!submittedTerm && !loading && rows.length === 0 && <Typography>No user found for &apos;{submittedTerm}&apos;.</Typography>}
          {!!submittedTerm && rows.length > 0 && (
            <CustomSortableTable
              title="Results"
              counter={rows.length}
              headers={[
                { key: 'name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'role', label: 'Role' },
                { key: 'organisation', label: 'Organisation' },
                { key: 'location', label: 'Location' },
                { key: 'active', label: 'Active' },
              ]}
              rows={tableRows}
            />
          )}
        </Grid>
      </Grid>
      {isAdmin && (
        <>
          <CustomDialog
            open={resetState.confirmOpen}
            title={resetState.name}
            subtitle={'This will email the user a fresh invite / password-reset link so they can set a new password. Continue?'}
            actions={[
              { label: 'Confirm', onClick: handleResetAccess },
              { label: 'Cancel', onClick: () => setResetState((prev) => ({ ...prev, confirmOpen: false })) },
            ]}
            handleClose={() => setResetState((prev) => ({ ...prev, confirmOpen: false }))}
          />
          <CustomDialog
            open={deleteState.confirmOpen}
            title={`Delete ${deleteState.name}?`}
            subtitle={
              "This permanently removes the user from the database and deletes their login. This cannot be undone. Users with clinical records (TBSA assessments / Dan's Fund patients) can't be deleted and should be deactivated instead."
            }
            actions={[
              { label: 'Delete permanently', onClick: handleDeleteUser },
              { label: 'Cancel', onClick: () => setDeleteState((prev) => ({ ...prev, confirmOpen: false })) },
            ]}
            handleClose={() => setDeleteState((prev) => ({ ...prev, confirmOpen: false }))}
          />
          <CustomDialog
            open={resultDialog.open}
            title={'Reset password'}
            subtitle={resultDialog.message}
            actions={[{ label: 'Close', onClick: () => setResultDialog({ open: false, message: '' }) }]}
            handleClose={() => setResultDialog({ open: false, message: '' })}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default UserLookup;
